import React, { CSSProperties, forwardRef, MouseEvent, useCallback, useRef } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { getPreviewEntityUniqueElementId, stopEvents } from "@mapmycustomers/shared/util/browser";

import ActivityTypeChip from "@app/component/activity/ActivityTypeChip";
import EntityChip from "@app/component/EntityChip";
import Lexer from "@app/component/Lexer";
import MultilineText from "@app/component/typography/MultilineText";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import { getAllTeamsOfEntireOrganization, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { TimelineEventActivityCompleted } from "@app/types/events/TimelineEvent";

import BaseEvent from "./BaseEvent";

export interface RecordEventProps {
  event: TimelineEventActivityCompleted;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  onShowEntityView: (payload: EntityViewPayload) => void;
  teams: Team[];
  users: User[];
}

const ActivityCompletedEvent = forwardRef<HTMLDivElement, Props>(
  ({ event, expanded, onShowEntityView, onSizeChange, style, teams, users }, ref) => {
    const intl = useIntl();

    const activityTypeChipRef = useRef<HTMLDivElement>(null);
    const handleActivityClick = useCallback(
      (_, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        const elementId = getPreviewEntityUniqueElementId(activityTypeChipRef.current || undefined);
        onShowEntityView({ elementId, entityId: event.entity.id, entityType: event.entity.entity });
      },
      [event, onShowEntityView]
    );

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    return (
      <BaseEvent
        date={event.updatedAt}
        expanded={expanded}
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.activityCompleted",
            defaultMessage:
              "completed a {activityType}{record, select, false {} other { with {record}}}",
            description: "Title of the Activity Completed event item",
          },
          {
            activityType: (
              <ActivityTypeChip
                activityType={event.entity.crmActivityType}
                isAutoEmail={!!event.entity.emailLog}
                onClick={handleActivityClick}
                ref={activityTypeChipRef}
              />
            ),
            record: !event.associatedEntity
              ? false
              : intl.formatList(
                  event.associatedEntity!.map((record) => (
                    <EntityChip
                      entityId={record.id}
                      entityType={record.entity}
                      inline
                      key={record.id}
                      name={record.name}
                      onClick={handleShowEntity}
                    />
                  )),
                  { type: "conjunction" }
                ),
          }
        )}
        user={event.updatedBy}
      >
        {event.entity.note ? (
          <div>
            <MultilineText wrapLinesWith={Lexer} wrapperProps={{ teams, users }}>
              {event.entity.note}
            </MultilineText>
          </div>
        ) : null}
      </BaseEvent>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  teams: getAllTeamsOfEntireOrganization(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  ActivityCompletedEvent
);
